/**
 * NATEX Live Data Hook
 *
 * Fetches real indicator data from World Bank API and tracks
 * which indicators need NATEX manual assessment.
 *
 * DATA TRANSPARENCY PRINCIPLE:
 * - Show ONLY real data from verified sources
 * - Never fake completeness with dummy values
 * - Clearly mark indicators awaiting manual assessment
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchIndicator, WBDataPoint } from '../services/api/worldBankApi';
import { CountryIndicatorData } from '../services/scoring/types';
import { ALL_INDICATORS, INDICATOR_BY_ID } from '../services/scoring';
import { DataSource } from '../services/scoring/types';
import { COUNTRY_DATA } from '../data/countryScoreData';

// Data source status types - HONEST categorization
export type DataSourceStatus = 'live' | 'cached' | 'natex_assessment' | 'pending' | 'failed';

export interface IndicatorDataPoint {
  id: string;
  value: number | null;
  year: number | null;
  source: 'World Bank' | 'IMF' | 'WGI' | 'NATEX Assessment' | 'Pending';
  sourceCode?: string;
  status: DataSourceStatus;
  lastFetched: Date | null;
  error?: string;
  requiresManualInput?: boolean;
}

export interface LiveDataResult {
  indicators: Record<string, IndicatorDataPoint>;
  countryData: CountryIndicatorData;
  isLoading: boolean;
  error: string | null;
  overallStatus: DataSourceStatus;
  liveCount: number;
  pendingAssessmentCount: number;
  pendingDataCount: number;
  failedCount: number;
  lastUpdated: Date | null;
  refetch: () => void;
  // Score transparency
  indicatorsWithData: number;
  totalIndicators: number;
}

// Mapping from our indicator IDs to World Bank codes
// ONLY indicators with verified API mappings
const INDICATOR_WB_MAPPING: Record<string, { code: string; transform?: (v: number) => number }> = {
  // Growth Drivers - Economic Momentum
  GDP_GROWTH_5Y: { code: 'NY.GDP.MKTP.KD.ZG' },

  // Growth Drivers - Demographics
  WORKING_AGE_POP: { code: 'SP.POP.1564.TO.ZS' },
  POPULATION_GROWTH: { code: 'SP.POP.GROW' },
  URBANIZATION_RATE: { code: 'SP.URB.TOTL.IN.ZS' },
  DEPENDENCY_RATIO: { code: 'SP.POP.DPND' },

  // Growth Drivers - Digital
  INTERNET_PENETRATION: { code: 'IT.NET.USER.ZS' },
  MOBILE_SUBSCRIPTIONS: { code: 'IT.CEL.SETS.P2' },

  // Investment Climate - Market
  MARKET_SIZE_GDP: { code: 'NY.GDP.MKTP.CD', transform: (v) => v / 1e9 },
  TRADE_OPENNESS: { code: 'NE.TRD.GNFS.ZS' },

  // Investment Climate - Operations
  DAYS_START_BUSINESS: { code: 'IC.REG.DURS' },
  TAX_BURDEN: { code: 'IC.TAX.TOTL.CP.ZS' },
  TERTIARY_ENROLLMENT: { code: 'SE.TER.ENRR' },

  // Risk Profile - Macro
  INFLATION_CPI: { code: 'FP.CPI.TOTL.ZG' },
  EXTERNAL_DEBT_GDP: { code: 'DT.DOD.DECT.GN.ZS' },

  // Risk Profile - External
  RESERVE_ADEQUACY: { code: 'FI.RES.TOTL.MO' },
  CURRENT_ACCOUNT: { code: 'BN.CAB.XOKA.GD.ZS' },
};

// NATEX Proprietary Assessment indicators - these REQUIRE manual input
// NOT fake data - these are assessments we need to make
const NATEX_ASSESSMENT_INDICATORS = new Set([
  // Economic calculations we derive
  'GDP_GROWTH_ACCELERATION',
  'GDP_GROWTH_STABILITY',
  'SECTOR_DIVERSIFICATION',
  'PRODUCTIVITY_GROWTH',

  // Digital economy assessments
  'DIGITAL_PAYMENTS_GROWTH',
  'ECOMMERCE_PENETRATION',
  'TECH_SECTOR_SIZE',

  // Policy assessments
  'POLICY_CONTINUITY',
  'REGULATORY_QUALITY',
  'GOVERNMENT_EFFECTIVENESS',
  'INVESTOR_PROTECTION',

  // Market access assessments
  'FTA_NETWORK',
  'REGIONAL_HUB_POTENTIAL',

  // Operations assessments
  'INFRASTRUCTURE_QUALITY',

  // Reform tracking (our proprietary tracking)
  'REFORM_COUNT_2Y',
  'REFORM_QUALITY',
  'REFORM_PIPELINE',

  // FDI pipeline assessments
  'ANNOUNCED_FDI',
  'PIPELINE_CONVERSION',
  'INVESTOR_SENTIMENT',

  // Event calendar assessments
  'RATING_UPGRADE_POTENTIAL',
  'ACCESSION_EVENTS',
  'POLITICAL_CALENDAR',

  // Political risk assessments
  'POLITICAL_STABILITY_WGI',
  'CONFLICT_RISK',
  'GEOPOLITICAL_RISK',

  // Macro risk assessments
  'CURRENCY_VOLATILITY',
  'SHORT_TERM_DEBT_RESERVES',

  // Demographics
  'MEDIAN_AGE',

  // Valuation assessments
  'FDI_GDP_VS_PEERS',
  'GROWTH_VS_RATING',
  'COST_ADVANTAGE',

  // Timing assessments
  'SCORE_MOMENTUM',
  'CATALYST_PROXIMITY',
  'INVESTOR_POSITIONING',
]);

// Storage key for NATEX assessments
const NATEX_ASSESSMENTS_STORAGE_KEY = 'natex_assessments';

// Get stored NATEX assessments
function getStoredAssessments(countryCode: string): Record<string, { value: number; date: string; analyst?: string }> {
  try {
    const stored = localStorage.getItem(`${NATEX_ASSESSMENTS_STORAGE_KEY}_${countryCode}`);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

// Country name mapping - Central Asia focused
const COUNTRY_NAMES: Record<string, string> = {
  UZB: 'Uzbekistan',
  KAZ: 'Kazakhstan',
  KGZ: 'Kyrgyzstan',
  TJK: 'Tajikistan',
  TKM: 'Turkmenistan',
  AZE: 'Azerbaijan',
  GEO: 'Georgia',
  MNG: 'Mongolia',
};

export function useNatexLiveData(countryCode: string): LiveDataResult {
  const [indicators, setIndicators] = useState<Record<string, IndicatorDataPoint>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchLiveData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const results: Record<string, IndicatorDataPoint> = {};
    const currentYear = new Date().getFullYear();
    const storedAssessments = getStoredAssessments(countryCode);

    try {
      // Fetch World Bank indicators in parallel
      const wbFetches = Object.entries(INDICATOR_WB_MAPPING).map(async ([indicatorId, config]) => {
        try {
          const response = await fetchIndicator(countryCode, config.code, currentYear - 10, currentYear);

          if (response.data && response.data.length > 0) {
            const latestWithValue = response.data.find(d => d.value !== null);

            if (latestWithValue && latestWithValue.value !== null) {
              const value = config.transform
                ? config.transform(latestWithValue.value)
                : latestWithValue.value;

              results[indicatorId] = {
                id: indicatorId,
                value: Math.round(value * 100) / 100,
                year: latestWithValue.year,
                source: 'World Bank',
                sourceCode: config.code,
                status: response.source === 'live' ? 'live' : 'cached',
                lastFetched: new Date(),
              };
              return;
            }
          }

          // No data from API - mark as pending
          results[indicatorId] = {
            id: indicatorId,
            value: null,
            year: null,
            source: 'Pending',
            sourceCode: config.code,
            status: 'pending',
            lastFetched: new Date(),
            error: 'No API data available',
          };
        } catch (err) {
          // Error - mark as failed
          results[indicatorId] = {
            id: indicatorId,
            value: null,
            year: null,
            source: 'Pending',
            sourceCode: config.code,
            status: 'failed',
            lastFetched: new Date(),
            error: err instanceof Error ? err.message : 'Fetch failed',
          };
        }
      });

      await Promise.all(wbFetches);

      // Process ALL indicators - mark NATEX assessment ones correctly
      for (const indicator of ALL_INDICATORS) {
        if (results[indicator.id]) {
          // Already processed from WB API
          continue;
        }

        const isNatexAssessment = NATEX_ASSESSMENT_INDICATORS.has(indicator.id) ||
          indicator.source === DataSource.NATEX;

        // Check if we have a stored manual assessment
        const storedValue = storedAssessments[indicator.id];

        if (storedValue) {
          results[indicator.id] = {
            id: indicator.id,
            value: storedValue.value,
            year: new Date(storedValue.date).getFullYear(),
            source: 'NATEX Assessment',
            status: 'natex_assessment',
            lastFetched: new Date(storedValue.date),
            requiresManualInput: true,
          };
        } else if (isNatexAssessment) {
          // Needs NATEX manual assessment - show as pending
          results[indicator.id] = {
            id: indicator.id,
            value: null,
            year: null,
            source: 'NATEX Assessment',
            status: 'natex_assessment',
            lastFetched: null,
            requiresManualInput: true,
            error: 'Awaiting NATEX assessment',
          };
        } else {
          // Other external data source - pending
          results[indicator.id] = {
            id: indicator.id,
            value: null,
            year: null,
            source: 'Pending',
            status: 'pending',
            lastFetched: null,
            error: 'Data source not connected',
          };
        }
      }

      setIndicators(results);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch live data');
    } finally {
      setIsLoading(false);
    }
  }, [countryCode]);

  // Fetch on mount and when country changes
  useEffect(() => {
    fetchLiveData();
  }, [fetchLiveData]);

  // Convert to CountryIndicatorData format
  // CRITICAL: Use static data as fallback when live data is incomplete
  const countryData = useMemo((): CountryIndicatorData => {
    const indicatorValues: Record<string, number | null> = {};
    const staticData = COUNTRY_DATA[countryCode];

    for (const [id, data] of Object.entries(indicators)) {
      // If live data is available, use it
      if (data.value !== null) {
        indicatorValues[id] = data.value;
      }
      // Otherwise use static data as fallback (this is the fix!)
      else if (staticData && staticData.indicators[id] !== undefined) {
        indicatorValues[id] = staticData.indicators[id];
      } else {
        indicatorValues[id] = null;
      }
    }

    // Also include any static indicators not covered by the fetch loop
    if (staticData) {
      for (const [id, value] of Object.entries(staticData.indicators)) {
        if (indicatorValues[id] === undefined || indicatorValues[id] === null) {
          indicatorValues[id] = value;
        }
      }
    }

    return {
      countryCode,
      countryName: COUNTRY_NAMES[countryCode] || countryCode,
      indicators: indicatorValues,
      asOfDate: lastUpdated || new Date(),
    };
  }, [indicators, countryCode, lastUpdated]);

  // Calculate stats - HONEST accounting (includes static data fallback)
  const stats = useMemo(() => {
    const values = Object.values(indicators);
    const staticData = COUNTRY_DATA[countryCode];

    // Live = actually fetched from API with real value
    const liveCount = values.filter(v =>
      (v.status === 'live' || v.status === 'cached') && v.value !== null
    ).length;

    // NATEX assessments that have values (manually entered)
    const natexWithData = values.filter(v =>
      v.status === 'natex_assessment' && v.value !== null
    ).length;

    // Count indicators that have fallback static data
    let staticFallbackCount = 0;
    if (staticData) {
      for (const indicator of ALL_INDICATORS) {
        const liveValue = indicators[indicator.id];
        const hasLiveData = liveValue && liveValue.value !== null;
        const hasStaticData = staticData.indicators[indicator.id] !== undefined && staticData.indicators[indicator.id] !== null;
        if (!hasLiveData && hasStaticData) {
          staticFallbackCount++;
        }
      }
    }

    // NATEX assessments pending input (that don't have static fallback)
    const pendingAssessmentCount = values.filter(v =>
      v.status === 'natex_assessment' && v.value === null &&
      (!staticData || staticData.indicators[v.id] === undefined || staticData.indicators[v.id] === null)
    ).length;

    // Other pending data (that don't have static fallback)
    const pendingDataCount = values.filter(v =>
      v.status === 'pending' && v.value === null &&
      (!staticData || staticData.indicators[v.id] === undefined || staticData.indicators[v.id] === null)
    ).length;

    const failedCount = values.filter(v => v.status === 'failed').length;

    // Total indicators with actual data (including static fallback)
    const indicatorsWithData = liveCount + natexWithData + staticFallbackCount;

    let overallStatus: DataSourceStatus = 'live';
    if (failedCount > values.length * 0.5 && staticFallbackCount === 0) {
      overallStatus = 'failed';
    } else if (pendingAssessmentCount + pendingDataCount > values.length * 0.5 && staticFallbackCount === 0) {
      overallStatus = 'pending';
    } else if (liveCount > 0 || staticFallbackCount > 0) {
      overallStatus = 'live';
    }

    return {
      liveCount: liveCount + staticFallbackCount, // Include static data in "live" count for display
      pendingAssessmentCount,
      pendingDataCount,
      failedCount,
      overallStatus,
      indicatorsWithData,
      totalIndicators: ALL_INDICATORS.length,
    };
  }, [indicators, countryCode]);

  return {
    indicators,
    countryData,
    isLoading,
    error,
    overallStatus: stats.overallStatus,
    liveCount: stats.liveCount,
    pendingAssessmentCount: stats.pendingAssessmentCount,
    pendingDataCount: stats.pendingDataCount,
    failedCount: stats.failedCount,
    lastUpdated,
    refetch: fetchLiveData,
    indicatorsWithData: stats.indicatorsWithData,
    totalIndicators: stats.totalIndicators,
  };
}

// Export function to save NATEX assessments
export function saveNatexAssessment(
  countryCode: string,
  indicatorId: string,
  value: number,
  analyst?: string
): void {
  const stored = getStoredAssessments(countryCode);
  stored[indicatorId] = {
    value,
    date: new Date().toISOString(),
    analyst,
  };
  localStorage.setItem(
    `${NATEX_ASSESSMENTS_STORAGE_KEY}_${countryCode}`,
    JSON.stringify(stored)
  );
}

// Export function to get all stored assessments for admin panel
export function getAllStoredAssessments(countryCode: string) {
  return getStoredAssessments(countryCode);
}

// Export function to clear assessments (for admin)
export function clearNatexAssessments(countryCode: string): void {
  localStorage.removeItem(`${NATEX_ASSESSMENTS_STORAGE_KEY}_${countryCode}`);
}

export default useNatexLiveData;
